import { useMutation } from "@apollo/client";
import { MessageCircle, Send, X } from "lucide-react";
import { FormEvent, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { SEND_CONTACT_SUPPORT_MAIL } from "src/graphql/mutations/user.mutation";
import useAppStore from "src/store/utils";
import { Mutation, MutationSendContactSupportMailArgs } from "src/types/api";
import Button from "../Button";
import Icon from "../Icon";

const SUPPORT_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL || "onaayomishi@inawo.co";
const SUPPORT_WHATSAPP =
  process.env.REACT_APP_SUPPORT_WHATSAPP_NUMBER || "2347041004669";
const SUPPORT_WHATSAPP_SECONDARY =
  process.env.REACT_APP_SUPPORT_WHATSAPP_NUMBER_2 || "2349126006869";
const MAX_MESSAGE_LENGTH = 1000;

const ContactSupport = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const wrapperRef = useRef<HTMLDivElement>(null);
  const eventId = useAppStore((state) => state.event.uuid);
  const [sendContactSupportMail, { loading: isSubmitting }] = useMutation<
    Pick<Mutation, "sendContactSupportMail">,
    MutationSendContactSupportMailArgs
  >(SEND_CONTACT_SUPPORT_MAIL);

  const trimmedMessage = message.trim();
  const encodedIntro = encodeURIComponent(
    "Hi Inawo, I need some help with my account."
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isOpen]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!trimmedMessage || isSubmitting) return;

    if (!eventId) {
      toast.error("Please select an event before sending a support message.");
      return;
    }

    try {
      const { data } = await sendContactSupportMail({
        variables: {
          event_id: eventId,
          input: {
            content: trimmedMessage,
            subject: "Inawo support request",
          },
        },
      });

      if (!data?.sendContactSupportMail) {
        throw new Error("The support message was not accepted.");
      }

      setMessage("");
      setIsOpen(false);
      toast.success("Your message has been sent to our support team.");
    } catch (error) {
      toast.error(
        "We couldn't send your message. Please try WhatsApp or email instead."
      );
    }
  };

  return (
    <div
      className="fixed bottom-[max(20px,env(safe-area-inset-bottom))] right-[max(20px,env(safe-area-inset-right))] z-[1000] font-['Inter',sans-serif] max-[480px]:bottom-[max(16px,env(safe-area-inset-bottom))] max-[480px]:right-4"
      ref={wrapperRef}
    >
      {isOpen && (
        <section
          className="mb-3 w-[min(400px,calc(100vw-32px))] overflow-hidden rounded-2xl border border-[#e8eeec] bg-white text-[#243b37] shadow-[0_18px_48px_rgb(21_47_42_/_20%)]"
          id="contact-support-panel"
          aria-labelledby="contact-support-title"
          role="dialog"
        >
          <div className="flex items-start justify-between gap-4 bg-[#f3f7f5] px-5 pb-4 pt-5">
            <div>
              <h2
                className="mb-1 text-lg font-bold leading-[1.35]"
                id="contact-support-title"
              >
                How can we help?
              </h2>
              <p className="m-0 text-[13px] leading-6 text-[#61736f]">
                Choose a channel or leave a message for our team.
              </p>
            </div>
            <button
              className="grid size-8 shrink-0 cursor-pointer place-items-center rounded-full border-0 bg-transparent p-0 text-[#4c625e] hover:bg-[#e4ece9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#78918b]"
              type="button"
              aria-label="Close contact support"
              onClick={() => setIsOpen(false)}
            >
              <X size={18} aria-hidden="true" />
            </button>
          </div>

          <div className="px-5 pb-5 pt-[18px]">
            <div className="grid grid-cols-2 gap-2.5">
              <a
                className="flex min-h-11 items-center justify-center gap-[9px] rounded-[9px] border border-[#d7e2df] bg-white px-3 py-2.5 text-sm font-semibold text-[#34544f] no-underline transition-colors hover:border-[#aebfba] hover:bg-[#f3f7f5] hover:text-[#243b37] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#78918b] motion-reduce:transition-none"
                href={`https://wa.me/${SUPPORT_WHATSAPP}?text=${encodedIntro}`}
                target="_blank"
                rel="noreferrer"
              >
                <Icon iconName="whatsapp" aria-hidden="true" />
                WhatsApp 1
              </a>
              <a
                className="flex min-h-11 items-center justify-center gap-[9px] rounded-[9px] border border-[#d7e2df] bg-white px-3 py-2.5 text-sm font-semibold text-[#34544f] no-underline transition-colors hover:border-[#aebfba] hover:bg-[#f3f7f5] hover:text-[#243b37] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#78918b] motion-reduce:transition-none"
                href={`https://wa.me/${SUPPORT_WHATSAPP_SECONDARY}?text=${encodedIntro}`}
                target="_blank"
                rel="noreferrer"
              >
                <Icon iconName="whatsapp" aria-hidden="true" />
                WhatsApp 2
              </a>
              <a
                className="col-span-2 flex min-h-11 items-center justify-center gap-[9px] rounded-[9px] border border-[#d7e2df] bg-white px-3 py-2.5 text-sm font-semibold text-[#34544f] no-underline transition-colors hover:border-[#aebfba] hover:bg-[#f3f7f5] hover:text-[#243b37] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#78918b] motion-reduce:transition-none"
                href={`mailto:${SUPPORT_EMAIL}?subject=Inawo%20support%20request`}
              >
                <Icon iconName="email" aria-hidden="true" />
                Email
              </a>
            </div>

            <div
              className="my-[18px] flex items-center gap-3"
              aria-hidden="true"
            >
              <span className="h-px flex-1 bg-[#d7e2df]" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-[#7a8c88]">
                Or
              </span>
              <span className="h-px flex-1 bg-[#d7e2df]" />
            </div>

            <form onSubmit={handleSubmit}>
              <label
                className="mb-[7px] block text-[13px] font-semibold"
                htmlFor="support-message"
              >
                Send us a message
              </label>
              <textarea
                className="block min-h-[104px] w-full resize-y rounded-[9px] border border-[#d7e2df] bg-[#fafcfb] p-3 text-xs leading-6 text-[#243b37] placeholder:text-[#8a9996] focus:border-[#6e8b84] focus:bg-white focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-[#a7bab5]"
                id="support-message"
                value={message}
                maxLength={MAX_MESSAGE_LENGTH}
                placeholder="Tell us what happened or how we can help..."
                onChange={(event) => setMessage(event.target.value)}
              />
              <div className="mt-3 flex items-center justify-between gap-3">
                <span className="text-[11px] text-[#7a8c88]" aria-live="polite">
                  {message.length}/{MAX_MESSAGE_LENGTH}
                </span>
                <Button
                  label={isSubmitting ? "Sending..." : "Send message"}
                  type="submit"
                  disabled={!trimmedMessage || isSubmitting}
                  loader={isSubmitting}
                  wrapperBtnClassName="!w-max"
                >
                  <span className="flex items-center gap-2">
                    <Send size={16} aria-hidden="true" />
                    Send message
                  </span>
                </Button>
              </div>
            </form>
          </div>
        </section>
      )}

      <button
        className={`ml-auto grid size-[52px] cursor-pointer place-items-center rounded-full border-0 bg-[#34544f] text-white shadow-[0_8px_24px_rgb(20_48_43_/_24%)] transition duration-200 hover:-translate-y-0.5 hover:opacity-100 hover:shadow-[0_10px_28px_rgb(20_48_43_/_32%)] focus-visible:-translate-y-0.5 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#78918b] motion-reduce:transition-none ${
          isOpen
            ? "-translate-y-0.5 opacity-100 shadow-[0_10px_28px_rgb(20_48_43_/_32%)]"
            : "opacity-[0.58]"
        }`}
        type="button"
        aria-label={isOpen ? "Close contact support" : "Contact support"}
        aria-expanded={isOpen}
        aria-controls="contact-support-panel"
        onClick={() => setIsOpen((open) => !open)}
      >
        {isOpen ? (
          <X size={23} aria-hidden="true" />
        ) : (
          <MessageCircle size={25} aria-hidden="true" />
        )}
      </button>
    </div>
  );
};

export default ContactSupport;
